import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import { AEROS, stateText } from "../../styles/aerosTokens";

import GroupIcon from "@material-ui/icons/Group";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import ContactTag from "../ContactTag";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  ListItem,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    // Card do mockup Aeros: surface, border 1px, radius 12, padding 12px 14px,
    // flex gap 12; selecionado com barra 3px gradiente (top/bottom 10, radius 3).
    ticket: {
      position: "relative",
      overflow: "hidden",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.border : "rgba(255,255,255,0.07)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      marginBottom: 8,
      transition: "background-color 0.15s ease, border-color 0.15s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 10,
        bottom: 10,
        left: 0,
        width: 0,
        borderRadius: 3,
        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
        transition: "width 0.15s ease",
      },
      "&:hover:not(.Mui-selected)": {
        backgroundColor: "rgba(160,68,255,0.04)",
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: "rgba(160,68,255,0.08)",
        borderColor: "rgba(160,68,255,0.3)",
      },
      "&.Mui-selected::before": {
        width: 3,
      },
      // Botões de ação só aparecem no hover do card (não existem no mockup)
      "&:hover $hoverActions": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },

    pendingTicket: {
      cursor: "unset",
    },

    avatarWrap: {
      position: "relative",
      flex: "none",
    },

    ticketAvatarFallback: {
      color: "#07080D",
      fontWeight: 700,
    },

    clickableAvatar: {
      cursor: "pointer",
      "&:hover": {
        opacity: 0.8,
      },
    },

    // Mini-badge do canal no canto inferior-direito do avatar (mockup):
    // círculo 16px com o bg da página e ícone do canal 11px.
    channelBadge: {
      position: "absolute",
      bottom: -1,
      right: -1,
      width: 16,
      height: 16,
      borderRadius: "50%",
      background: theme.palette.aeros.background,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    contentWrap: {
      flex: 1,
      minWidth: 0,
    },

    line1: {
      display: "flex",
      alignItems: "baseline",
      minWidth: 0,
    },

    contactName: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },

    time: {
      marginLeft: "auto",
      flex: "none",
      paddingLeft: 8,
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.aeros.textTertiary,
    },

    timeUnread: {
      color: AEROS.cyan,
      fontWeight: 700,
    },

    lastMessage: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginTop: 2,
      "& p": {
        margin: 0,
        display: "inline",
      },
    },

    lastMessageUnread: {
      fontWeight: 600,
    },

    metaRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginTop: 7,
      minWidth: 0,
      overflow: "hidden",
    },

    // Pills da linha 3 (fila/conexão/atendente): 10px/600, radius 5, 1px 7px
    pill: {
      fontSize: 10,
      fontWeight: 600,
      borderRadius: 5,
      padding: "1px 7px",
      whiteSpace: "nowrap",
      flex: "none",
      maxWidth: 120,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    pillNeutral: {
      backgroundColor: isLight ? "rgba(15,20,40,0.07)" : "rgba(255,255,255,0.07)",
      color: theme.palette.text.secondary,
    },

    ticketIdTag: {
      fontSize: 10,
      fontWeight: 600,
      color: theme.palette.aeros.textTertiary,
      flex: "none",
    },

    unreadBadge: {
      marginLeft: "auto",
      flex: "none",
      background: theme.palette.aerosGradient,
      color: "#07080D",
      fontSize: 10,
      fontWeight: 800,
      borderRadius: 99,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    // Overlay de ações visível só no hover do card
    hoverActions: {
      position: "absolute",
      right: 10,
      bottom: 8,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "2px 4px",
      borderRadius: 8,
      background: theme.palette.aeros.surface2,
      border: `1px solid ${theme.palette.aeros.border}`,
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 0.15s ease",
    },

    // Estilos para o modal da imagem
    imageModal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    imageModalContent: {
      outline: "none",
      maxWidth: "90vw",
      maxHeight: "90vh",
    },
    expandedImage: {
      width: "100%",
      height: "auto",
      maxWidth: "500px",
      borderRadius: theme.spacing(1),
    },
  };
});

// Botões do overlay de hover: 26×26, ícone 16px, cor por ação
const actionButtonStyle = (color) => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  border: "none",
  padding: 0,
  minWidth: 26,
  width: 26,
  height: 26,
  borderRadius: 7,
  color,
});

const avatarColorFor = (id) => AEROS.avatarCycle[id % AEROS.avatarCycle.length];
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
    return `rgba(124,124,124,${alpha})`;
  }
  const clean = hex.replace("#", "").slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const userHasSaleFlag = (u) =>
  u?.finalizacaoComValorVendaAtiva === true ||
  u?.finalizacaoComValorVendaAtiva === "true";

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Setting de "exigir motivo" buscada sob demanda (evita 1 request por item da lista)
    let requireReason = false;
    if (!userHasSaleFlag(user)) {
      try {
        const reasonSetting = await getSetting({
          column: "requireFinalizationReason",
        });
        requireReason = !!reasonSetting?.requireFinalizationReason;
      } catch (err) {
        requireReason = false;
      }
    }

    // Verificar se a finalização com valor de venda está ativa
    if (userHasSaleFlag(user) || requireReason) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Localização</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        dense
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        {/* Avatar 42px com mini-badge do canal (mockup) */}
        <div className={classes.avatarWrap}>
          <Avatar
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: ticket?.contact?.urlPicture
                ? undefined
                : avatarColorFor(ticket?.contact?.id || ticket?.id || 0),
            }}
            src={`${ticket?.contact?.urlPicture}`}
            className={clsx(classes.clickableAvatar, {
              [classes.ticketAvatarFallback]: !ticket?.contact?.urlPicture,
            })}
            onClick={handleImageClick}
          >
            {!ticket?.contact?.urlPicture && initialsOf(ticket?.contact?.name)}
          </Avatar>
          {ticket.channel && (
            <div className={classes.channelBadge}>
              {ticket.channel === "whatsapp" && (
                <WhatsAppIcon style={{ fontSize: 11, color: "#25D366" }} />
              )}
              {ticket.channel === "facebook" && (
                <FacebookIcon style={{ fontSize: 11, color: "#4267B2" }} />
              )}
              {ticket.channel === "instagram" && (
                <InstagramIcon style={{ fontSize: 11, color: "#E1306C" }} />
              )}
            </div>
          )}
        </div>

        <div className={classes.contentWrap}>
          {/* Linha 1: nome + horário à direita */}
          <div className={classes.line1}>
            <span className={classes.contactName}>
              {ticket.isGroup && ticket.channel === "whatsapp" && (
                <GroupIcon
                  style={{
                    fontSize: 14,
                    color: theme.palette.text.secondary,
                    marginRight: 4,
                    verticalAlign: "text-bottom",
                  }}
                />
              )}
              {truncate(ticket.contact?.name, 60)}
            </span>
            {ticket.lastMessage && (
              <span
                className={clsx(classes.time, {
                  [classes.timeUnread]: Number(ticket.unreadMessages) > 0,
                })}
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                  <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                ) : (
                  <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                )}
              </span>
            )}
          </div>

          {/* Linha 2: última mensagem */}
          <div
            className={clsx(classes.lastMessage, {
              [classes.lastMessageUnread]: Number(ticket.unreadMessages) > 0,
            })}
          >
            {renderLastMessage()}
          </div>

          {/* Linha 3: pill da fila + pills extras + #id + badge de não-lidas */}
          <div className={classes.metaRow}>
            <span
              className={classes.pill}
              style={{
                backgroundColor: withAlpha(ticket.queue?.color || "#7c7c7c", 0.1),
                color: stateText(theme, ticket.queue?.color || "#7c7c7c"),
              }}
            >
              {ticket.queueId
                ? ticket.queue?.name.toUpperCase()
                : ticket.status === "lgpd"
                ? "LGPD"
                : `${i18n.t("momentsUser.noqueue")}`}
            </span>
            {ticket?.whatsapp && (
              <span
                className={classes.pill}
                style={{
                  backgroundColor: withAlpha(
                    ticket.channel === "whatsapp"
                      ? ticket.whatsapp?.color || "#25D366"
                      : ticket.channel === "facebook"
                      ? "#4267B2"
                      : "#E1306C",
                    0.1
                  ),
                  color: stateText(
                    theme,
                    ticket.channel === "whatsapp"
                      ? ticket.whatsapp?.color || "#25D366"
                      : ticket.channel === "facebook"
                      ? "#4267B2"
                      : "#E1306C"
                  ),
                }}
              >
                {ticket.whatsapp?.name.toUpperCase()}
              </span>
            )}
            {ticket?.user && (
              <span className={clsx(classes.pill, classes.pillNeutral)}>
                {ticket.user?.name.toUpperCase()}
              </span>
            )}
            <span className={classes.ticketIdTag}>#{ticket.id}</span>
            {ticket?.contact?.tags?.map((tag) => {
              return (
                <ContactTag
                  tag={tag}
                  key={`ticket-contact-tag-${ticket.id}-${tag.id}`}
                />
              );
            })}
            {ticket.tags?.map((tag) => {
              return (
                <ContactTag
                  tag={tag}
                  key={`ticket-tag-${ticket.id}-${tag.id}`}
                />
              );
            })}
            {(Number(ticket.unreadMessages) > 0 || shouldBlurMessages) && (
              <span className={classes.unreadBadge}>
                {shouldBlurMessages ? "?" : ticket.unreadMessages}
              </span>
            )}
          </div>
        </div>

        {/* Ações do card: visíveis apenas no hover (não existem no mockup) */}
        <div className={classes.hoverActions}>
          {/* Para tickets com status chatbot, mostrar apenas o ícone de spy */}
          {ticket.status === "chatbot" && (
            <ButtonWithSpinner
              style={actionButtonStyle(stateText(theme, AEROS.cyan, "info"))}
              variant="contained"
              size="small"
              loading={loading}
              onClick={(e) => {
                e.stopPropagation();
                handleSpyTicket();
              }}
            >
              <Tooltip title="Espiar conversa do chatbot">
                <VisibilityIcon style={{ fontSize: 16 }} />
              </Tooltip>
            </ButtonWithSpinner>
          )}

          {/* Para todos os outros status, manter os botões originais */}
          {ticket.status !== "chatbot" && (
            <>
              {ticket.status === "pending" &&
                (ticket.queueId === null || ticket.queueId === undefined) && (
                  <ButtonWithSpinner
                    style={actionButtonStyle(stateText(theme, AEROS.success, "success"))}
                    variant="contained"
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenAcceptTicketWithouSelectQueue();
                    }}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Done style={{ fontSize: 16 }} />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              {ticket.status === "pending" && ticket.queueId !== null && (
                <ButtonWithSpinner
                  style={actionButtonStyle(stateText(theme, AEROS.success, "success"))}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcepptTicket(ticket.id);
                  }}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                    <Done style={{ fontSize: 16 }} />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "pending" ||
                ticket.status === "open" ||
                ticket.status === "group") && (
                <ButtonWithSpinner
                  style={actionButtonStyle(theme.palette.primary.main)}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTransferModal();
                  }}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                    <SwapHoriz style={{ fontSize: 16 }} />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "open" || ticket.status === "group") && (
                <ButtonWithSpinner
                  style={actionButtonStyle(stateText(theme, AEROS.error, "error"))}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseTicket(ticket.id);
                  }}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                    <HighlightOff style={{ fontSize: 16 }} />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "pending" || ticket.status === "lgpd") &&
                (user.userClosePendingTicket === "enabled" ||
                  user.profile === "admin") && (
                  <ButtonWithSpinner
                    style={actionButtonStyle(stateText(theme, AEROS.error, "error"))}
                    variant="contained"
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseIgnoreTicket(ticket.id);
                    }}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                      <HighlightOff style={{ fontSize: 16 }} />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              {ticket.status === "closed" && (
                <ButtonWithSpinner
                  style={actionButtonStyle(stateText(theme, AEROS.success, "success"))}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNewTicketModal();
                  }}
                >
                  <Tooltip title="Criar Novo Ticket">
                    <Add style={{ fontSize: 16 }} />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
            </>
          )}
        </div>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img 
            src={ticket?.contact?.urlPicture} 
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;